/* eslint-disable no-undef, no-restricted-globals */
import $ from 'jquery';
import _ from 'underscore';
import * as urlUtil from '@bingads-webui/url-util';
import { http } from '@bingads-webui/http-util';
import {
  KeepAliveDependencies, JQueryAjaxSettings, UnAuthCallbackFunction, KeepAliveOptions, KeepAliveRequestError,
  KeepAliveResponse,
} from '../types';

const checkFrequencyInMinutes = 5;

export function fullFillUrl(
  relativeUrl: string,
  location: Pick<Location, 'href' | 'hostname' | 'origin' | 'protocol'> = window.location
): string {
  if (location.hostname.toLowerCase() === 'app1.localhost.ccui.devdev.bingads.microsoft.com') {
    return `${location.origin}${relativeUrl}`;
  }
  const domain = urlUtil.getDomain(location.href);
  return `${location.protocol}//${domain.replace(/ui\.(netcore\.)?/i, '')}${relativeUrl}`;
}

export class KeepAlive {
  checkFrequency: number;

  deps: KeepAliveDependencies;

  keepAliveCounter: number;

  keepAliveMaxCount: number;

  keepAliveTask: ReturnType<typeof setInterval> | null;

  requestOption: JQueryAjaxSettings;

  unAuthCallBack: UnAuthCallbackFunction;

  url: string;

  /**
   * initialize Keep Alive Class
   * @param {KeepAliveOptions} options - options for keep alive
   * @returns {void}
   */
  constructor({
    deps = {
      httpUtil: http,
    },
    url = '/root/keepalive?__adcenterAJAX=true',
    requestOption = {
      converters: {
        // eslint-disable-next-line func-names
        'text json': function (data: string) {
          if (_.isEmpty(data)) {
            return null;
          }
          return JSON.parse(data);
        },
      },
      xhrFields: {
        withCredentials: true,
      },
      contentType: 'application/x-www-form-urlencoded',
    },
    reWriteUrl = fullFillUrl,
    timeoutInMinutes = 240,
    unAuthCallBack = _.identity as UnAuthCallbackFunction,
  }: KeepAliveOptions) {
    this.url = url;
    if (urlUtil.isRelativeUrl(url)) {
      this.url = reWriteUrl(url);
    }
    this.deps = deps;
    this.keepAliveTask = null;
    this.checkFrequency = checkFrequencyInMinutes * 1000 * 60;
    this.keepAliveCounter = 0;
    this.keepAliveMaxCount = timeoutInMinutes / checkFrequencyInMinutes;
    this.unAuthCallBack = unAuthCallBack;
    this.requestOption = requestOption;
  }

  issueKeepAliveRequest(): PromiseLike<boolean> {
    return this.deps.httpUtil.get(this.url, this.requestOption).then((data = {}) => {
      const response = data as KeepAliveResponse;
      if (_.result(response, 'Error') === '401') {
        this.unAuthCallBack(response);
        return false;
      }
      return true;
    }).catch((reason: KeepAliveRequestError) => {
      if (_.result(reason, 'status') === 200) {
        return true;
      }
      return false;
    }).then((result: boolean) => {
      if (result) {
        this.keepAliveCounter += 1;
      }
      return result;
    });
  }

  issueKeepAliveRequestWithExceptionHandling(): void {
    try {
      this.issueKeepAliveRequest();
    // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  start(): void {
    this.stop();
    this.issueKeepAliveRequestWithExceptionHandling();
    this.keepAliveTask = setInterval(() => {
      if (this.keepAliveCounter <= this.keepAliveMaxCount) {
        this.issueKeepAliveRequestWithExceptionHandling();
      }
    }, this.checkFrequency);

    $(window).on('unload', () => this.stop());
  }

  stop(): void {
    if (this.keepAliveTask !== null) {
      clearInterval(this.keepAliveTask);
      this.keepAliveTask = null;
    }
  }

  refresh(): void {
    this.keepAliveCounter = 0;
  }
}

export const keepAliveInstance = new KeepAlive({});
